import { MoreHorizontal, Settings2, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/shared/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu'
import { useWidgetMutations } from '../mutations'

/**
 * What can be done to one widget.
 *
 * **Deleting is not confirmed**, which is the same judgement the grid's row
 * delete was given: a deleted widget goes to the workspace's trash, where it is
 * already named and restorable, so the question a confirmation would ask has an
 * answer that costs two clicks rather than a lost afternoon. Upstream does not
 * confirm it either.
 */
export function WidgetMenu({
  dashboardId,
  widgetId,
  title,
  onConfigure,
}: {
  dashboardId: number
  widgetId: number
  /** Named in the menu's label, so a board of them is distinguishable. */
  title: string
  onConfigure: () => void
}) {
  const { t } = useTranslation()
  const { remove, isPending } = useWidgetMutations(dashboardId)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-7 shrink-0"
          disabled={isPending}
          aria-label={t('widget.actions', { title })}
        >
          <MoreHorizontal />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={onConfigure}>
          <Settings2 />
          {t('widget.configure')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          variant="destructive"
          onSelect={() => {
            remove(widgetId)
          }}
        >
          <Trash2 />
          {t('action.delete')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
